import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import '../Signin/login.css';
import { BR, US } from 'country-flag-icons/react/3x2';
import i18n from '../../i18n';
import { useNavigate } from 'react-router-dom';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [usingHashSession, setUsingHashSession] = useState(false);

  // Idiomas
  const languageOptions = {
    pt: { label: 'Português', flag: BR, country: 'Brasil' },
    en: { label: 'English', flag: US, country: 'United States' },
  };
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'pt');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    if (i18n.changeLanguage) {
      i18n.changeLanguage(lang);
    }
    setShowDropdown(false);
  };

  useEffect(() => {
    if (i18n.changeLanguage) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  const CurrentFlag = languageOptions[language]?.flag;

  // Checa hash e cria sessão quando for fluxo de recovery no hash
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.replace('#', ''));
    const errorParam = params.get('error');
    const errorDesc = params.get('error_description');
    if (errorParam) {
      if (errorParam === 'access_denied' && errorDesc?.includes('expired')) {
        setError(t('resetPassword.error') || 'O link de redefinição expirou ou é inválido. Solicite um novo link.');
      } else {
        setError(decodeURIComponent(errorDesc || t('resetPassword.error')));
      }
    }

    const typeParam = params.get('type');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (typeParam === 'recovery' && accessToken && refreshToken) {
      (async () => {
        try {
          setIsVerifying(true);
          setError('');
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionError) {
            setError(sessionError.message || t('resetPassword.error'));
            return;
          }
          setUsingHashSession(true);
          if (window.history?.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          }
        } catch (_) {
          setError(t('resetPassword.error'));
        } finally {
          setIsVerifying(false);
        }
      })();
    }
  }, [t]);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!password || !confirm) {
      setError(t('resetPassword.error') || 'Preencha todos os campos.');
      return;
    }
    if (password.length < 6) {
      setError(t('resetPassword.error') || 'A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError(t('resetPassword.error') || 'As senhas não coincidem.');
      return;
    }
    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (updateError) {
      if (updateError.message?.toLowerCase().includes('expired')) {
        setError(t('resetPassword.error') || 'O link de redefinição expirou ou é inválido. Solicite um novo link.');
      } else {
        setError(updateError.message || t('resetPassword.error'));
      }
    } else {
      setSuccess(t('resetPassword.success'));
      setPassword('');
      setConfirm('');
      // Redireciona para home após alguns segundos
      setTimeout(() => {
        navigate('/home');
      }, 1200);
    }
  };

  return (
    <div id="login-page">
      <div id="login-container">
        {/* Seletor de idioma e botão voltar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 18, position: 'relative' }} ref={dropdownRef}>
          <button
            type="button"
            className="language-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: '#23272f',
              border: '1px solid #4B5563',
              borderRadius: 6,
              padding: '6px 12px',
              color: '#fff',
              fontSize: 15,
              cursor: 'pointer'
            }}
            onClick={() => setShowDropdown(!showDropdown)}
            title={languageOptions[language]?.country}
          >
            {CurrentFlag && <CurrentFlag className="flag-icon" style={{ width: 22, marginRight: 8 }} />}
            <span>{languageOptions[language]?.label}</span>
            <svg style={{ marginLeft: 8, width: 14, height: 14, fill: '#fff' }} viewBox="0 0 20 20"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z"/></svg>
          </button>
          <button
            type="button"
            onClick={() => navigate('/home')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: '#3E6F5C',
              border: 'none',
              borderRadius: 6,
              padding: '6px 12px',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#35604c'}
            onMouseLeave={(e) => e.target.style.background = '#3E6F5C'}
          >
            <svg style={{ width: 16, height: 16, fill: '#fff' }} viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd"/>
            </svg>
            <span>{language === 'pt' ? 'Voltar' : 'Back'}</span>
          </button>
          {showDropdown && (
            <div
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                background: '#23272f',
                border: '1px solid #4B5563',
                borderRadius: 8,
                zIndex: 10,
                minWidth: 160,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
              }}
            >
              {Object.entries(languageOptions).map(([code, { label, flag: Flag, country }]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: 15,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    borderBottom: code !== 'en' ? '1px solid #374151' : 'none',
                    fontWeight: code === language ? 700 : 400
                  }}
                  title={country}
                >
                  <Flag className="flag-icon" style={{ width: 22 }} />
                  <span>{label}</span>
                  {code === language && <span style={{ marginLeft: 'auto', color: '#10b981' }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <form id="login-form" onSubmit={handleReset}>
          <div className="input-group">
            <label className="input-label">{t('resetPassword.newPassword')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon icon={faLock} className="input-icon" />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input-field"
                placeholder={t('resetPassword.newPassword')}
                minLength={6}
                required
              />
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">{t('resetPassword.confirmNewPassword')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon icon={faLock} className="input-icon" />
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="input-field"
                placeholder={t('resetPassword.confirmNewPassword')}
                minLength={6}
                required
              />
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div style={{ color: '#10b981', fontSize: 15 }}>{success}</div>}
          <button
            type="submit"
            disabled={loading}
            className="login-button"
            style={{ marginTop: 8 }}
          >
            {loading ? t('resetPassword.loading') : t('resetPassword.save')}
          </button>
        </form>
        <p style={{ fontSize: 14, color: '#999', marginTop: 24 }}>
          {t('resetPassword.ignore')}
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
